import { baseApi } from '../baseApi';

export const userExerciseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Student Exercise Routes
    getStudentExercises: builder.query({
      query: (params = {}) => ({
        url: '/student/exercises',
        params: {
          page: params.page,
          limit: params.limit,
        },
      }),
      providesTags: ['StudentExercise'],
    }),
    
    // Get exercise detail for student
    getStudentExerciseById: builder.query({
      query: (exerciseId) => `/student/exercises/${exerciseId}`,
      providesTags: (result, error, exerciseId) => [
        { type: 'StudentExercise', id: exerciseId }
      ],
    }),

    getExerciseDay: builder.query({
      query: ({ exerciseId, dayId }) => `/student/exercises/${exerciseId}/days/${dayId}`,
      providesTags: (result, error, { exerciseId, dayId }) => [
        { type: 'ExerciseDay', id: `${exerciseId}-${dayId}` }
      ],
    }),

    // Student Vocabulary
    getStudentVocabularyWords: builder.query({
      query: ({ exerciseId, dayId, page, limit }) => ({
        url: `/student/days/${dayId}/vocabulary`,
        params: {
          page,
          limit,
        },
      }),
      providesTags: (result, error, { exerciseId, dayId }) => [
        { type: 'StudentVocabulary', id: `${exerciseId}-${dayId}` }
      ],
    }),

    // Student Vocabulary Questions (Section-based)
    getStudentVocabularyQuestions: builder.query({
      query: ({ exerciseId, dayId }) => 
        `/student/days/${dayId}/vocabulary/questions`,
      providesTags: (result, error, { exerciseId, dayId }) => [
        { type: 'StudentVocabularyQuestion', id: `${exerciseId}-${dayId}` }
      ],
    }),
    submitAnswer: builder.mutation({
      query: ({ exerciseId, dayId, questionId, answer, partType }) => ({
        url: `/student/exercises/${exerciseId}/days/${dayId}/questions/${questionId}/answer`,
        method: 'POST',
        body: { answer, partType },
      }),
      invalidatesTags: (result, error, { exerciseId, dayId }) => [
        { type: 'ExerciseDay', id: `${exerciseId}-${dayId}` }
      ],
    }),
    getStudentProgress: builder.query({
      query: ({ exerciseId, dayId }) => `/student/exercises/${exerciseId}/days/${dayId}/progress`,
      providesTags: (result, error, { exerciseId, dayId }) => [
        { type: 'ExerciseDay', id: `${exerciseId}-${dayId}` }
      ],
    }),
    markPartCompleted: builder.mutation({
      query: ({ exerciseId, dayId, partType }) => ({
        url: `/student/exercises/${exerciseId}/days/${dayId}/complete-part`,
        method: 'POST',
        body: { partType },
      }),
      invalidatesTags: (result, error, { exerciseId, dayId }) => [
        { type: 'ExerciseDay', id: `${exerciseId}-${dayId}` }
      ],
    }),
    getStudentStatistics: builder.query({
      query: () => '/student/statistics',
      providesTags: ['StudentStatistics'],
    }),

    // Student Exercise Statistics (grouped by day → section)
    getStudentExerciseStatistics: builder.query({
      query: () => '/student/exercise-statistics',
      providesTags: ['StudentExerciseStats'],
    }),

    // Exercise Session Management
    startExerciseSession: builder.mutation({
      query: ({ dayId, section, deviceInfo }) => ({
        url: `/student/days/${dayId}/sections/${section}/start`,
        method: 'POST',
        body: deviceInfo ? { deviceInfo } : undefined,
      }),
      invalidatesTags: (result, error, { dayId, section }) => [
        { type: 'ExerciseSession', id: `${dayId}-${section}` }
      ],
    }),
    submitExerciseAnswer: builder.mutation({
      query: ({ sessionId, questionId, selectedOption, timeSpent }) => ({
        url: `/student/exercise-sessions/${sessionId}/answer`,
        method: 'POST',
        body: { questionId, selectedOption, timeSpent },
      }),
      invalidatesTags: (result, error, { sessionId }) => [
        { type: 'ExerciseSession', id: sessionId }
      ],
    }),
    completeExerciseSession: builder.mutation({
      query: (sessionId) => ({
        url: `/student/exercise-sessions/${sessionId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, sessionId) => [
        { type: 'ExerciseSession', id: sessionId },
        'ExerciseAttempts'
      ],
    }),
    getExerciseSession: builder.query({
      query: (sessionId) => `/student/exercise-sessions/${sessionId}`,
      providesTags: (result, error, sessionId) => [
        { type: 'ExerciseSession', id: sessionId }
      ],
    }),
    resumeExerciseSession: builder.mutation({
      query: (sessionId) => ({
        url: `/student/exercise-sessions/${sessionId}/resume`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, sessionId) => [
        { type: 'ExerciseSession', id: sessionId }
      ],
    }),

    // Student LC/RC Exercise Questions
    getStudentExerciseQuestions: builder.query({
      query: ({ dayId, partType }) => {
        // Convert ex: lcExercise -> lc-exercise
        const sectionType = partType.replace(/([A-Z])/g, "-$1").toLowerCase();
        return `/student/days/${dayId}/sections/${sectionType}/questions`;
      },
      providesTags: (result, error, { dayId, partType }) => [
        { type: 'StudentExerciseQuestions', id: `${dayId}-${partType}` }
      ],
    }),

    // Student Dialogue Question Groups
    getStudentDialogueQuestionGroup: builder.query({
      query: (parentQuestionId) => `/student/dialogue-questions/${parentQuestionId}`,
      providesTags: (result, error, parentQuestionId) => [
        { type: 'StudentDialogueQuestion', id: parentQuestionId }
      ],
    }),
    validateDialogueGroupAnswers: builder.mutation({
      query: ({ parentQuestionId, answers }) => ({
        url: `/student/dialogue-questions/${parentQuestionId}/validate`,
        method: 'POST',
        body: { answers },
      }),
    }),
    getStudentDialogueQuestionsBySection: builder.query({
      query: ({ section, exerciseDay, partNumber }) => ({
        url: `/student/dialogue-questions/section/${section}`,
        params: { exerciseDay, partNumber },
      }),
      providesTags: ['StudentDialogueQuestion'],
    }),
  }),
});

export const {
  // Student Exercise Routes
  useGetStudentExercisesQuery,
  useLazyGetStudentExercisesQuery,
  useGetStudentExerciseByIdQuery,
  useLazyGetStudentExerciseByIdQuery,
  useGetExerciseDayQuery,
  useLazyGetExerciseDayQuery,
  
  // Student Vocabulary
  useGetStudentVocabularyWordsQuery,
  useLazyGetStudentVocabularyWordsQuery,
  useGetStudentVocabularyQuestionsQuery,
  useLazyGetStudentVocabularyQuestionsQuery,
  
  // Student Progress & Answers
  useSubmitAnswerMutation,
  useGetStudentProgressQuery,
  useLazyGetStudentProgressQuery,
  useMarkPartCompletedMutation,
  useGetStudentStatisticsQuery,
  useGetStudentExerciseStatisticsQuery,

  // Exercise Session Management
  useStartExerciseSessionMutation,
  useSubmitExerciseAnswerMutation,
  useCompleteExerciseSessionMutation,
  useGetExerciseSessionQuery,
  useLazyGetExerciseSessionQuery,
  useGetUserExerciseAttemptsQuery,
  useInterruptExerciseSessionMutation,
  useResumeExerciseSessionMutation,

  // Student LC/RC Exercise Questions
  useGetStudentExerciseQuestionsQuery,
  useLazyGetStudentExerciseQuestionsQuery,

  // Student Dialogue Question Groups
  useGetStudentDialogueQuestionGroupQuery,
  useValidateDialogueGroupAnswersMutation,
  useGetStudentDialogueQuestionsBySectionQuery,
} = userExerciseApi;
